<template>
  <div class="competence-category-picker" ref="pickerContainer">
    <!-- Closed State / Trigger -->
    <div 
      class="picker-trigger"
      :class="{ 'picker-trigger--open': isOpen }"
      @click="toggleDropdown"
      tabindex="0"
      @keydown.enter="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
      @keydown.escape="closeDropdown"
    >
      <div class="picker-content">
        <CompetenceCategoryIcon :icon="selectedCategory" class="picker-icon" />
        <span class="picker-label">{{ categoryLabel }}</span>
      </div>
      <v-icon class="picker-chevron" :class="{ 'picker-chevron--rotated': isOpen }">
        mdi-chevron-down
      </v-icon>
    </div>

    <!-- Dropdown Menu -->
    <div v-if="isOpen" class="picker-dropdown">
      <div class="picker-menu">
        <div
          v-for="option in categoryOptions"
          :key="option.value"
          class="picker-menu-item"
          :class="{ 
            'picker-menu-item--selected': selectedCategory === option.value 
          }"
          @click="selectCategory(option.value)"
          tabindex="0"
          @keydown.enter="selectCategory(option.value)"
        >
          <div class="picker-menu-item-content">
            <CompetenceCategoryIcon :icon="option.value" class="picker-menu-icon" />
            <span class="picker-menu-label">{{ option.label }}</span>
          </div>
          <img 
            v-if="selectedCategory === option.value"
            src="@components/assets/rightIcon.svg" 
            alt="selected" 
            class="picker-check-icon"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import CompetenceCategoryIcon from "@components/CompetenceCategoryIcon/CompetenceCategoryIcon.vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: "knowledge",
    validator: (value) => ["knowledge", "ability", "behaviour"].includes(value),
  },
  open: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["update:modelValue", "update:open", "change"]);

const pickerContainer = ref(null);
const isOpen = ref(props.open);

const categoryOptions = [
  { value: "knowledge", label: "Knowledge" },
  { value: "ability", label: "Ability" },
  { value: "behaviour", label: "Behavior" },
];

// Local state for selected category - allows component to work standalone
const selectedCategory = ref(props.modelValue);

const categoryLabel = computed(() => {
  const option = categoryOptions.find(
    (opt) => opt.value === selectedCategory.value
  );
  return option ? option.label : "Knowledge";
});

// Sync with parent's modelValue prop changes
watch(
  () => props.modelValue,
  (newVal) => {
    selectedCategory.value = newVal;
  }
);

watch(
  () => props.open,
  (newVal) => {
    isOpen.value = newVal;
  }
);

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  emit("update:open", isOpen.value);
};

const closeDropdown = () => {
  isOpen.value = false;
  emit("update:open", false);
};

const selectCategory = (categoryValue) => {
  selectedCategory.value = categoryValue; // Update local state immediately
  emit("update:modelValue", categoryValue);
  emit("change", categoryValue);
  closeDropdown();
};

const handleClickOutside = (event) => {
  if (pickerContainer.value && !pickerContainer.value.contains(event.target)) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.competence-category-picker {
  position: relative;
  width: 184px;
  font-family: Inter, sans-serif;
}

.picker-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.picker-trigger:focus,
.picker-trigger--open {
  border-color: #d6bbfb;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05),
    0px 0px 0px 4px rgba(158, 119, 237, 0.24);
}

.picker-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.picker-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.picker-label {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.picker-chevron {
  flex-shrink: 0;
  color: #667085 !important;
  font-size: 20px !important;
  transition: transform 0.2s ease;
}

.picker-chevron--rotated {
  transform: rotate(180deg);
}

.picker-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.08),
    0px 4px 6px -2px rgba(16, 24, 40, 0.03);
  overflow: hidden;
}

.picker-menu {
  padding: 4px 0;
}

.picker-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 10px 10px 8px;
  margin: 1px 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  outline: none;
}

.picker-menu-item:hover {
  background-color: #f9fafb;
}

.picker-menu-item:focus {
  background-color: #f9fafb;
}

.picker-menu-item--selected {
  background-color: #f9fafb;
}

.picker-menu-item-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.picker-menu-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.picker-menu-label {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.picker-check-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}
</style>

